import { deepResearchService } from '../../services/apiServices/deepResearchService';
import { DeepResearch, DeepResearchResponse, DeepResearchStatus } from '../../libs/types/src/entities/DeepResearch';
import { message } from 'antd';

export const START_RESEARCH_REQUEST = 'START_RESEARCH_REQUEST';
export const START_RESEARCH_SUCCESS = 'START_RESEARCH_SUCCESS';
export const START_RESEARCH_FAILURE = 'START_RESEARCH_FAILURE';
export const GET_RESEARCH_REQUEST = 'GET_RESEARCH_REQUEST';
export const GET_RESEARCH_SUCCESS = 'GET_RESEARCH_SUCCESS';
export const GET_RESEARCH_FAILURE = 'GET_RESEARCH_FAILURE';
export const CLEAR_RESEARCH_ERROR = 'CLEAR_RESEARCH_ERROR';
export const CLEAR_CHAT_ERROR = 'CLEAR_CHAT_ERROR';
export const CLEAR_CHAT_HISTORY = 'CLEAR_CHAT_HISTORY';
export const CHAT_REQUEST = 'CHAT_REQUEST';
export const CHAT_SUCCESS = 'CHAT_SUCCESS';
export const CHAT_FAILURE = 'CHAT_FAILURE';
export const INJECT_USER_MESSAGE = 'INJECT_USER_MESSAGE';
export const GET_CHAT_MESSAGES_REQUEST = 'GET_CHAT_MESSAGES_REQUEST';
export const GET_CHAT_MESSAGES_SUCCESS = 'GET_CHAT_MESSAGES_SUCCESS';
export const GET_CHAT_MESSAGES_FAILURE = 'GET_CHAT_MESSAGES_FAILURE';
export const AGENT_STATUS_UPDATE = 'AGENT_STATUS_UPDATE';
export const AGENT_STATUS_RESET = 'AGENT_STATUS_RESET';
export const SET_ONGOING_RESEARCH_STATUS = 'SET_ONGOING_RESEARCH_STATUS';
export const CLEAR_RESEARCH_HISTORY = 'CLEAR_RESEARCH_HISTORY';
export const SET_RESEARCH_STATUS = 'SET_RESEARCH_STATUS';
export const EXPORT_DOCUMENT_REQUEST = 'EXPORT_DOCUMENT_REQUEST';
export const EXPORT_DOCUMENT_SUCCESS = 'EXPORT_DOCUMENT_SUCCESS';
export const EXPORT_DOCUMENT_FAILURE = 'EXPORT_DOCUMENT_FAILURE';

export const deepResearchActions = {
  startResearch,
  getResearch,
  clearResearchHistory,
  clearResearchError,
  clearChatError,
  clearChatHistory,
  chat,
  getChatMessages,
  checkOngoingResearchStatus,
  setOngoingResearchStatus,
  syncResearchStatus,
  exportDocument,
};

function startResearch(seller_company_name: string, prospect_company_name: string, prospect_id: number, user_guide: string, research_level: string,) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await deepResearchService.startResearch({
        seller_company_name,
        prospect_company_name,
        prospect_id,
        user_guide,
        research_level
      });
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to start research'));
    }
  };

  function request() {
    return { type: START_RESEARCH_REQUEST };
  }
  function success(research: DeepResearch) {
    return { type: START_RESEARCH_SUCCESS, research };
  }
  function failure(error: string) {
    return { type: START_RESEARCH_FAILURE, error };
  }
}

function getResearch(prospect_id: number, page: number, pageSize: number) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await deepResearchService.getResearch({ prospect_id, page, page_size: pageSize });
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to fetch research'));
    }
  };

  function request() {
    return { type: GET_RESEARCH_REQUEST };
  }
  function success(research: DeepResearchResponse) {
    return { type: GET_RESEARCH_SUCCESS, research };
  }
  function failure(error: string) {
    return { type: GET_RESEARCH_FAILURE, error };
  }
}

function clearResearchError() {
  return (dispatch: any) => {
    dispatch({ type: CLEAR_RESEARCH_ERROR });
  };
}

function syncResearchStatus() {
  return async (dispatch: any, getState: any) => {

    const history = getState().deepResearch.researchHistory;
    const ids = history.filter((research: any) => ![DeepResearchStatus.PROCESSED, DeepResearchStatus.FAILED].includes(research.status)).map((research: any) => research.id);
    if (ids.length === 0) return;
    const result = await deepResearchService.getResearch({ id__in: ids.join(',') });
    dispatch(success(result.data));
  }
  function success(response: any) {
    return { type: SET_RESEARCH_STATUS, response };
  }
}

function clearChatError() {
  return (dispatch: any) => {
    dispatch({ type: CLEAR_CHAT_ERROR });
  };
}

function clearChatHistory() {
  return (dispatch: any) => {
    dispatch({ type: CLEAR_CHAT_HISTORY });
  };
}

function getChatMessages(campaign_id: number, user_id: number, receiver_id: number, page: number, pageSize: number) {

  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await deepResearchService.getChatMessages(campaign_id, user_id, receiver_id, page, pageSize);
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to fetch chat messages'));
    }
  };

  function request() {
    return { type: GET_CHAT_MESSAGES_REQUEST };
  }
  function success(messages: { data: any[], metadata: any }) {
    messages.data = messages.data?.map((message: any) => {
      const result: any[] = [];
      if (message.final_result) {
        result.push({ type: 'ai', response: message.final_result });
      }
      result.push({ type: 'user', query: message.user_query });

      return result;
    })?.flat()
    messages.metadata = {
      count: messages.metadata.count,
      page_size: messages.metadata.page_size,
      page_count: messages.metadata.page_count,
      has_next: messages.metadata.has_next,
      next: messages.metadata.next,
    }
    return { type: GET_CHAT_MESSAGES_SUCCESS, messages };
  }
  function failure(error: string) {
    return { type: GET_CHAT_MESSAGES_FAILURE, error };
  }
}

function chat(query: string, user_id: number, receiver_id: number, campaign_id: number) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      dispatch(injectUserMessage(query));
      const result = await deepResearchService.chat(query, user_id, receiver_id, campaign_id);

      // Create a readable stream from the response
      const reader = result.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process SSE events
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventText of events) {
          if (eventText.trim() && eventText.startsWith('data: ')) {
            try {
              const jsonData = eventText.substring(6);
              const data = JSON.parse(jsonData);

              if (data.type === 'chunk') {
                dispatch(agentStatusUpdate(data.data));
                // Update your UI here
              } else if (data.type === 'final_output') {
                dispatch(agentStatusReset());
                dispatch(success(data.data));

                // Final result received
                break;
              } else if (data.type === 'error') {
                console.error('Error:', data.error);
                throw new Error(data.error);
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', eventText);
            }
          }
        }
      }
    } catch (e) {
      dispatch(failure(e.message || 'Failed to chat'));
    }
  };

  function injectUserMessage(message: string) {
    return (dispatch: any) => {
      dispatch({ type: INJECT_USER_MESSAGE, message });
    };
  }

  function request() {
    return { type: CHAT_REQUEST };
  }
  function success(response: any) {
    return { type: CHAT_SUCCESS, response };
  }
  function failure(error: string) {
    return { type: CHAT_FAILURE, error };
  }
  function agentStatusUpdate(status: string) {
    return { type: AGENT_STATUS_UPDATE, status };
  }
  function agentStatusReset() {
    return { type: AGENT_STATUS_RESET };
  }
}

function checkOngoingResearchStatus(prospect_id: number) {
  return async (dispatch: any) => {
    const result = await deepResearchService.getResearchStatus({ prospect_id });
    dispatch(success(result.data.status));
  }
  function success(status: string) {
    return { type: SET_ONGOING_RESEARCH_STATUS, isProcessing: status === 'processing' };
  }
}

function setOngoingResearchStatus(status: boolean) {
  return { type: SET_ONGOING_RESEARCH_STATUS, isProcessing: status };
}

function clearResearchHistory() {
  return { type: CLEAR_RESEARCH_HISTORY };
}

function exportDocument(id: string, type: string, fileName: string) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const response = await deepResearchService.exportDocument({ id, type });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName}.${type}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      dispatch(success());
      message.success('Download completed successfully!');
    } catch (e) {
      dispatch(failure());
      message.error('Failed to download document');
    }
  }
  function request() {
    return { type: EXPORT_DOCUMENT_REQUEST };
  }
  function success() {
    return { type: EXPORT_DOCUMENT_SUCCESS };
  }
  function failure() {
    return { type: EXPORT_DOCUMENT_FAILURE };
  }
}